import { useEffect, useState, useCallback } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Users, Building2, DollarSign, FileText, Upload, Download, Trash2, Search, Loader2,
  UserCheck, ClipboardList, Calculator, Wallet, Receipt, Save, Plus, IdCard, CreditCard, Award, ScrollText
} from 'lucide-react';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

const EMPTY_STRUCT = { basic: 0, hra: 0, da: 0, special_allowance: 0, conveyance: 0, medical: 0, other_allowance: 0, epf_employee_pct: 12, professional_tax: 200 };
const EMPTY_PROFILE = {
  department: '', designation: '', date_of_birth: '', gender: '', address: '', city: '', state: '', pincode: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  pan_number: '', aadhaar_number: '',
  bank_name: '', bank_account_number: '', bank_ifsc: '', bank_account_holder: '',
  epf_number: '', uan_number: '', esi_number: '',
};

const DOC_LABELS = { aadhaar: 'Aadhaar Card', pan: 'PAN Card', bank_passbook: 'Bank Passbook', cv: 'CV / Resume', photo: 'Profile Photo', other: 'Other' };

const statusColor = (s) => ({
  active: 'bg-green-100 text-green-700', inactive: 'bg-slate-100 text-slate-700',
  generated: 'bg-amber-100 text-amber-700', paid: 'bg-green-100 text-green-700',
}[s] || 'bg-slate-100 text-slate-700');

export default function HrPayrollPage() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // full employee w/ profile+docs+structure
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('personal');

  // Editable state
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [struct, setStruct] = useState(EMPTY_STRUCT);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingStruct, setSavingStruct] = useState(false);

  // Documents
  const [uploadingDoc, setUploadingDoc] = useState('');

  // Payroll
  const [payrolls, setPayrolls] = useState([]);
  const [payForm, setPayForm] = useState({ period: currentMonth(), working_days: 30, days_present: 30, incentive: 0, bonus: 0, other_earnings: 0, loss_of_pay_days: 0, other_deductions: 0, notes: '' });
  const [generating, setGenerating] = useState(false);
  const [downloadingPay, setDownloadingPay] = useState('');

  // ID card + Letters
  const [downloadingCard, setDownloadingCard] = useState(false);
  const [letterDialog, setLetterDialog] = useState(false);
  const [letterForm, setLetterForm] = useState({
    letter_type: 'experience',
    relieving_date: new Date().toISOString().split('T')[0],
    reason: '',
    remarks: '',
    salary_snapshot: 0,
    place: 'Rampurhat, West Bengal',
    signatory_name: '',
    signatory_designation: 'HR Manager',
  });
  const [issuingLetter, setIssuingLetter] = useState(false);

  const downloadIdCard = async () => {
    if (!selected) return;
    setDownloadingCard(true);
    try {
      const res = await api.get(`/hr/employees/${selected.id}/id-card`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `id-card-${selected.employee_code || selected.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('ID card downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDownloadingCard(false); }
  };

  const issueLetter = async () => {
    if (!selected) return;
    setIssuingLetter(true);
    try {
      const payload = { ...letterForm, salary_snapshot: Number(letterForm.salary_snapshot) || 0 };
      const res = await api.post(`/hr/employees/${selected.id}/experience-letter`, payload, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const label = letterForm.letter_type;
      a.href = url; a.download = `${label}-letter-${selected.employee_code || selected.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} letter downloaded`);
      setLetterDialog(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || 'Failed to issue letter'); }
    finally { setIssuingLetter(false); }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const [uRes, dRes] = await Promise.all([
        api.get('/users'),
        api.get('/hr/departments').catch(() => ({ data: { departments: [] } })),
      ]);
      setEmployees((uRes.data || []).filter(u => u.role !== 'investor' && u.role !== 'student'));
      setDepartments(dRes.data?.departments || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const openEmployee = async (id) => {
    try {
      const { data } = await api.get(`/hr/employees/${id}/profile`);
      setSelected(data);
      setProfile({ ...EMPTY_PROFILE, ...Object.fromEntries(Object.keys(EMPTY_PROFILE).map(k => [k, data[k] || ''])) });
      const s = data.salary_structure || EMPTY_STRUCT;
      setStruct({ ...EMPTY_STRUCT, ...s });
      // Load payrolls for this employee
      const pr = await api.get('/hr/payroll', { params: { employee_id: id } });
      setPayrolls(pr.data || []);
      setTab('personal');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveProfile = async () => {
    if (!selected) return;
    setSavingProfile(true);
    try {
      await api.patch(`/hr/employees/${selected.id}/profile`, profile);
      toast.success('Profile saved');
      openEmployee(selected.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const saveStructure = async () => {
    if (!selected) return;
    setSavingStruct(true);
    try {
      const clean = Object.fromEntries(Object.entries(struct).map(([k, v]) => [k, Number(v) || 0]));
      const { data } = await api.put(`/hr/employees/${selected.id}/salary-structure`, clean);
      toast.success(`Salary structure saved (Gross: ${money(data.gross_monthly)})`);
      openEmployee(selected.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSavingStruct(false); }
  };

  const uploadDocument = async (docType, file) => {
    if (!selected || !file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploadingDoc(docType);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/hr/employees/${selected.id}/documents/upload?doc_type=${docType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${DOC_LABELS[docType]} uploaded`);
      openEmployee(selected.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploadingDoc(''); }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await api.get(`/hr/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement('a');
      a.href = url; a.download = doc.original_filename || `${doc.doc_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete ${DOC_LABELS[doc.doc_type] || doc.doc_type}?`)) return;
    try {
      await api.delete(`/hr/documents/${doc.id}`);
      toast.success('Document deleted');
      openEmployee(selected.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const generatePayroll = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const payload = { ...payForm, employee_id: selected.id, incentive: Number(payForm.incentive) || 0, bonus: Number(payForm.bonus) || 0, other_earnings: Number(payForm.other_earnings) || 0, other_deductions: Number(payForm.other_deductions) || 0, working_days: Number(payForm.working_days) || 30, days_present: Number(payForm.days_present) || 30, loss_of_pay_days: Number(payForm.loss_of_pay_days) || 0 };
      const { data } = await api.post('/hr/payroll/generate', payload);
      toast.success(`Payroll ${payForm.period} generated — Net ${money(data.net_pay)}`);
      openEmployee(selected.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const downloadPayslip = async (pr) => {
    setDownloadingPay(pr.id);
    try {
      const res = await api.get(`/hr/payroll/${pr.id}/payslip.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `payslip-${pr.period}-${pr.employee_name.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDownloadingPay(''); }
  };

  const markPaid = async (pr) => {
    try {
      await api.post(`/hr/payroll/${pr.id}/pay`);
      toast.success('Marked paid');
      openEmployee(selected.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const filtered = employees.filter(e => {
    const s = search.toLowerCase();
    return !s || (e.name || '').toLowerCase().includes(s) || (e.email || '').toLowerCase().includes(s) || (e.department || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4" data-testid="hr-payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center"><Users size={20} className="text-white" /></div>
          <div>
            <h1 className="font-heading text-xl font-bold text-brand-text-primary">HR / Payroll</h1>
            <p className="text-xs text-brand-text-secondary">Employee profiles, documents, salary structure, attendance and monthly payslips</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* LEFT: employee list */}
        <Card className="border-brand-border bg-white h-fit">
          <CardHeader className="pb-2 border-b border-brand-border">
            <CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Employees</CardTitle>
            <div className="relative mt-2">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
              <Input placeholder="Search name / email / dept" value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-xs pl-7" data-testid="hr-search" />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center"><Loader2 size={20} className="animate-spin mx-auto text-brand-primary" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-xs text-brand-text-secondary">No employees. Create one on the Employees page.</div>
            ) : (
              <ul className="divide-y divide-brand-border">
                {filtered.map(e => (
                  <li
                    key={e.id}
                    onClick={() => openEmployee(e.id)}
                    className={`p-3 cursor-pointer hover:bg-brand-bg/60 transition-colors ${selected?.id === e.id ? 'bg-brand-primary/5 border-l-2 border-brand-primary' : ''}`}
                    data-testid={`hr-emp-${e.id}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-text-primary truncate">{e.name}</p>
                        <p className="text-[10px] text-brand-text-secondary truncate">{e.email}</p>
                        <p className="text-[10px] text-brand-primary uppercase tracking-wider mt-0.5">{(e.role || '').replace(/_/g, ' ')}</p>
                      </div>
                      {e.department && <Badge className="text-[9px] bg-brand-bg text-brand-text-primary border-0">{e.department}</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* RIGHT: profile editor */}
        {!selected ? (
          <Card className="border-brand-border bg-white">
            <CardContent className="p-16 text-center">
              <UserCheck size={48} className="mx-auto text-brand-text-secondary/40 mb-4" />
              <p className="text-brand-text-primary font-semibold mb-1">No employee selected</p>
              <p className="text-sm text-brand-text-secondary">Pick someone from the left to manage their HR profile, documents, salary structure and payslips.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Header card */}
            <Card className="border-brand-border bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <CardContent className="p-4 flex items-center flex-wrap gap-4">
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center font-heading text-2xl font-bold">
                  {(selected.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="mr-auto">
                  <p className="font-heading text-lg font-bold">{selected.name}</p>
                  <p className="text-xs text-white/70">{selected.email} · {(selected.role || '').replace(/_/g, ' ')}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {selected.employee_code && (
                      <span className="text-[11px] font-mono bg-white/10 text-white px-2 py-0.5 rounded" data-testid="employee-code">
                        {selected.employee_code}
                      </span>
                    )}
                    <p className="text-xs text-white/60">
                      {profile.department || '—'} {profile.designation && `· ${profile.designation}`} {selected.joining_date && `· Joined ${selected.joining_date}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={downloadIdCard} disabled={downloadingCard} className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white text-xs h-8" data-testid="download-id-card-btn">
                    {downloadingCard ? <Loader2 size={12} className="animate-spin mr-1" /> : <IdCard size={12} className="mr-1" />} ID Card
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setLetterDialog(true)} className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white text-xs h-8" data-testid="issue-letter-btn">
                    <ScrollText size={12} className="mr-1" /> Issue Letter
                  </Button>
                  <Badge className={`border-0 text-[10px] ${statusColor(selected.status)}`}>{(selected.status || '-').toUpperCase()}</Badge>
                </div>
              </CardContent>
            </Card>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-white border border-brand-border rounded-lg p-1 h-auto flex-wrap gap-1">
                <TabsTrigger value="personal" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="hr-tab-personal"><IdCard size={13} className="mr-1" /> Personal</TabsTrigger>
                <TabsTrigger value="documents" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="hr-tab-documents"><FileText size={13} className="mr-1" /> Documents ({selected.documents?.length || 0})</TabsTrigger>
                <TabsTrigger value="salary" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="hr-tab-salary"><Calculator size={13} className="mr-1" /> Salary Structure</TabsTrigger>
                <TabsTrigger value="payroll" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="hr-tab-payroll"><Wallet size={13} className="mr-1" /> Payroll ({payrolls.length})</TabsTrigger>
              </TabsList>

              {/* PERSONAL */}
              <TabsContent value="personal" className="mt-4">
                <Card className="border-brand-border bg-white">
                  <CardHeader className="pb-2 border-b border-brand-border flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">Personal & Bank Details</CardTitle>
                    <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="bg-brand-primary text-white text-xs h-8" data-testid="hr-save-profile">
                      {savingProfile ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />} Save Profile
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Department</Label>
                        <Select value={profile.department || undefined} onValueChange={v => setProfile({ ...profile, department: v })}>
                          <SelectTrigger className="h-9 text-sm" data-testid="hr-department"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Designation</Label><Input value={profile.designation} onChange={e => setProfile({ ...profile, designation: e.target.value })} className="h-9 text-sm" data-testid="hr-designation" /></div>
                      <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={profile.date_of_birth} onChange={e => setProfile({ ...profile, date_of_birth: e.target.value })} className="h-9 text-sm" /></div>
                      <div>
                        <Label className="text-xs">Gender</Label>
                        <Select value={profile.gender || undefined} onValueChange={v => setProfile({ ...profile, gender: v })}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">PAN</Label><Input value={profile.pan_number} onChange={e => setProfile({ ...profile, pan_number: e.target.value.toUpperCase() })} className="h-9 text-sm font-mono" placeholder="ABCDE1234F" data-testid="hr-pan" /></div>
                      <div><Label className="text-xs">Aadhaar</Label><Input value={profile.aadhaar_number} onChange={e => setProfile({ ...profile, aadhaar_number: e.target.value.replace(/\D/g, '') })} className="h-9 text-sm font-mono" placeholder="12-digit" maxLength={12} data-testid="hr-aadhaar" /></div>
                    </div>
                    <div>
                      <Label className="text-xs">Address</Label>
                      <Textarea value={profile.address} onChange={e => setProfile({ ...profile, address: e.target.value })} rows={2} data-testid="hr-address" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div><Label className="text-xs">City</Label><Input value={profile.city} onChange={e => setProfile({ ...profile, city: e.target.value })} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs">State</Label><Input value={profile.state} onChange={e => setProfile({ ...profile, state: e.target.value })} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs">PIN Code</Label><Input value={profile.pincode} onChange={e => setProfile({ ...profile, pincode: e.target.value })} className="h-9 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><Label className="text-xs">Emergency Contact Name</Label><Input value={profile.emergency_contact_name} onChange={e => setProfile({ ...profile, emergency_contact_name: e.target.value })} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs">Emergency Contact Phone</Label><Input value={profile.emergency_contact_phone} onChange={e => setProfile({ ...profile, emergency_contact_phone: e.target.value })} className="h-9 text-sm" /></div>
                    </div>

                    <div className="pt-3 border-t border-brand-border">
                      <p className="text-xs font-semibold text-brand-text-secondary uppercase tracking-widest mb-2 flex items-center gap-1"><CreditCard size={12} /> Bank Details</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Bank Name</Label><Input value={profile.bank_name} onChange={e => setProfile({ ...profile, bank_name: e.target.value })} className="h-9 text-sm" data-testid="hr-bank-name" /></div>
                        <div><Label className="text-xs">Account Holder Name</Label><Input value={profile.bank_account_holder} onChange={e => setProfile({ ...profile, bank_account_holder: e.target.value })} className="h-9 text-sm" /></div>
                        <div><Label className="text-xs">Account Number</Label><Input value={profile.bank_account_number} onChange={e => setProfile({ ...profile, bank_account_number: e.target.value.replace(/\D/g, '') })} className="h-9 text-sm font-mono" data-testid="hr-bank-acc" /></div>
                        <div><Label className="text-xs">IFSC</Label><Input value={profile.bank_ifsc} onChange={e => setProfile({ ...profile, bank_ifsc: e.target.value.toUpperCase() })} className="h-9 text-sm font-mono uppercase" placeholder="SBIN0000123" /></div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-brand-border">
                      <p className="text-xs font-semibold text-brand-text-secondary uppercase tracking-widest mb-2">Statutory</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div><Label className="text-xs">EPF Number</Label><Input value={profile.epf_number} onChange={e => setProfile({ ...profile, epf_number: e.target.value })} className="h-9 text-sm font-mono" /></div>
                        <div><Label className="text-xs">UAN Number</Label><Input value={profile.uan_number} onChange={e => setProfile({ ...profile, uan_number: e.target.value })} className="h-9 text-sm font-mono" /></div>
                        <div><Label className="text-xs">ESI Number</Label><Input value={profile.esi_number} onChange={e => setProfile({ ...profile, esi_number: e.target.value })} className="h-9 text-sm font-mono" /></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* DOCUMENTS */}
              <TabsContent value="documents" className="mt-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {['aadhaar', 'pan', 'bank_passbook', 'cv', 'photo', 'other'].map(dt => (
                    <Card key={dt} className="border-brand-border bg-white">
                      <CardContent className="p-4">
                        <p className="text-sm font-semibold text-brand-text-primary mb-1">{DOC_LABELS[dt]}</p>
                        <p className="text-[11px] text-brand-text-secondary mb-3">JPG/PNG/PDF · max 10MB</p>
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*,.pdf" className="hidden" data-testid={`hr-upload-${dt}`} onChange={e => uploadDocument(dt, e.target.files?.[0])} />
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-brand-primary-hover">
                            {uploadingDoc === dt ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
                          </span>
                        </label>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="border-brand-border bg-white">
                  <CardHeader className="pb-2 border-b border-brand-border"><CardTitle className="text-sm">Uploaded Documents</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {(selected.documents || []).length === 0 ? (
                      <div className="p-8 text-center text-sm text-brand-text-secondary">No documents uploaded yet.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                          {['Type', 'File', 'Size', 'Uploaded', 'Actions'].map(h => <th key={h} className="text-left py-2 px-4 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {selected.documents.map(d => (
                            <tr key={d.id} className="border-b border-brand-border/50" data-testid={`hr-doc-row-${d.id}`}>
                              <td className="py-2 px-4"><Badge className="text-[10px] border-0 bg-slate-100 text-slate-700">{DOC_LABELS[d.doc_type] || d.doc_type}</Badge></td>
                              <td className="py-2 px-4 text-xs">{d.original_filename}</td>
                              <td className="py-2 px-4 text-xs font-mono">{d.size_mb || (d.size_bytes / 1024 / 1024).toFixed(2)} MB</td>
                              <td className="py-2 px-4 text-xs text-brand-text-secondary">{(d.uploaded_at || '').slice(0, 10)}</td>
                              <td className="py-2 px-4 flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => downloadDocument(d)} className="h-7 text-xs" data-testid={`hr-doc-download-${d.id}`}><Download size={12} className="mr-1" /> Download</Button>
                                <Button size="sm" variant="outline" onClick={() => deleteDocument(d)} className="h-7 text-xs text-rose-600 border-rose-200"><Trash2 size={12} /></Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* SALARY STRUCTURE */}
              <TabsContent value="salary" className="mt-4">
                <Card className="border-brand-border bg-white">
                  <CardHeader className="pb-2 border-b border-brand-border flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">Monthly Salary Structure (₹)</CardTitle>
                    <Button size="sm" onClick={saveStructure} disabled={savingStruct} className="bg-brand-primary text-white text-xs h-8" data-testid="hr-save-structure">
                      {savingStruct ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />} Save Structure
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        ['basic', 'Basic Pay *'], ['hra', 'HRA'], ['da', 'Dearness Allowance'],
                        ['special_allowance', 'Special Allowance'], ['conveyance', 'Conveyance'],
                        ['medical', 'Medical'], ['other_allowance', 'Other Allowance'],
                        ['epf_employee_pct', 'EPF % (of Basic)'], ['professional_tax', 'Professional Tax (flat)'],
                      ].map(([k, label]) => (
                        <div key={k}>
                          <Label className="text-xs">{label}</Label>
                          <Input type="number" min="0" step="0.01" value={struct[k]} onChange={e => setStruct({ ...struct, [k]: Number(e.target.value) || 0 })} className="h-9 text-sm font-mono" data-testid={`hr-struct-${k}`} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-brand-border grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="p-3 rounded-lg bg-brand-bg">
                        <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary">Gross Monthly</p>
                        <p className="font-heading text-xl font-bold text-brand-text-primary mt-1">{money(Number(struct.basic) + Number(struct.hra) + Number(struct.da) + Number(struct.special_allowance) + Number(struct.conveyance) + Number(struct.medical) + Number(struct.other_allowance))}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-rose-50">
                        <p className="text-[10px] uppercase tracking-widest text-rose-700">Standard Deductions</p>
                        <p className="font-heading text-xl font-bold text-rose-700 mt-1">{money(Number(struct.basic) * Number(struct.epf_employee_pct || 0) / 100 + Number(struct.professional_tax || 0))}</p>
                        <p className="text-[10px] text-rose-600 mt-0.5">EPF {struct.epf_employee_pct}% + PT ₹{struct.professional_tax}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-green-50">
                        <p className="text-[10px] uppercase tracking-widest text-green-800">Estimated Net Pay</p>
                        <p className="font-heading text-xl font-bold text-green-800 mt-1">{money(Number(struct.basic) + Number(struct.hra) + Number(struct.da) + Number(struct.special_allowance) + Number(struct.conveyance) + Number(struct.medical) + Number(struct.other_allowance) - (Number(struct.basic) * Number(struct.epf_employee_pct || 0) / 100 + Number(struct.professional_tax || 0)))}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* PAYROLL */}
              <TabsContent value="payroll" className="mt-4 space-y-4">
                <Card className="border-brand-border bg-white">
                  <CardHeader className="pb-2 border-b border-brand-border"><CardTitle className="text-sm flex items-center gap-2"><Plus size={14} /> Generate Monthly Payslip</CardTitle></CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><Label className="text-xs">Period</Label><Input type="month" value={payForm.period} onChange={e => setPayForm({ ...payForm, period: e.target.value })} className="h-9 text-sm" data-testid="pay-period" /></div>
                      <div><Label className="text-xs">Working Days</Label><Input type="number" min="1" max="31" value={payForm.working_days} onChange={e => setPayForm({ ...payForm, working_days: e.target.value })} className="h-9 text-sm" data-testid="pay-working-days" /></div>
                      <div><Label className="text-xs">Days Present</Label><Input type="number" min="0" max="31" value={payForm.days_present} onChange={e => setPayForm({ ...payForm, days_present: e.target.value })} className="h-9 text-sm" data-testid="pay-days-present" /></div>
                      <div><Label className="text-xs">LOP Days</Label><Input type="number" min="0" step="0.5" value={payForm.loss_of_pay_days} onChange={e => setPayForm({ ...payForm, loss_of_pay_days: e.target.value })} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs">Incentive (₹)</Label><Input type="number" min="0" value={payForm.incentive} onChange={e => setPayForm({ ...payForm, incentive: e.target.value })} className="h-9 text-sm font-mono" data-testid="pay-incentive" /></div>
                      <div><Label className="text-xs">Bonus (₹)</Label><Input type="number" min="0" value={payForm.bonus} onChange={e => setPayForm({ ...payForm, bonus: e.target.value })} className="h-9 text-sm font-mono" /></div>
                      <div><Label className="text-xs">Other Earnings (₹)</Label><Input type="number" min="0" value={payForm.other_earnings} onChange={e => setPayForm({ ...payForm, other_earnings: e.target.value })} className="h-9 text-sm font-mono" /></div>
                      <div><Label className="text-xs">Other Deductions (₹)</Label><Input type="number" min="0" value={payForm.other_deductions} onChange={e => setPayForm({ ...payForm, other_deductions: e.target.value })} className="h-9 text-sm font-mono" /></div>
                    </div>
                    <div className="mt-3"><Label className="text-xs">Notes (optional)</Label><Textarea value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} rows={2} /></div>
                    <div className="mt-3 flex justify-end">
                      <Button onClick={generatePayroll} disabled={generating} className="bg-brand-primary text-white" data-testid="generate-payroll-btn">
                        {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />} Generate Payslip
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-brand-border bg-white">
                  <CardHeader className="pb-2 border-b border-brand-border"><CardTitle className="text-sm">Payroll History</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {payrolls.length === 0 ? (
                      <div className="p-8 text-center text-sm text-brand-text-secondary">No payslips generated yet.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                          {['Period', 'Days', 'Gross', 'Deductions', 'Net Pay', 'Status', 'Actions'].map(h => <th key={h} className="text-left py-2 px-4 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {payrolls.map(pr => (
                            <tr key={pr.id} className="border-b border-brand-border/50" data-testid={`payroll-row-${pr.id}`}>
                              <td className="py-2 px-4 font-mono text-xs">{pr.period}</td>
                              <td className="py-2 px-4 text-xs">{pr.days_present}/{pr.working_days}</td>
                              <td className="py-2 px-4 font-mono text-xs">{money(pr.gross_earnings)}</td>
                              <td className="py-2 px-4 font-mono text-xs text-rose-600">- {money(pr.total_deductions)}</td>
                              <td className="py-2 px-4 font-mono text-xs font-bold text-brand-text-primary">{money(pr.net_pay)}</td>
                              <td className="py-2 px-4"><Badge className={`text-[10px] border-0 ${statusColor(pr.status)}`}>{(pr.status || '').toUpperCase()}</Badge></td>
                              <td className="py-2 px-4 flex gap-1">
                                <Button size="sm" onClick={() => downloadPayslip(pr)} disabled={downloadingPay === pr.id} className="bg-brand-primary text-white text-xs h-7" data-testid={`download-payslip-${pr.id}`}>
                                  {downloadingPay === pr.id ? <Loader2 size={12} className="animate-spin" /> : <><Download size={12} className="mr-1" /> PDF</>}
                                </Button>
                                {pr.status !== 'paid' && <Button size="sm" variant="outline" onClick={() => markPaid(pr)} className="text-xs h-7 text-green-700 border-green-200">Mark Paid</Button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* LETTER ISSUANCE DIALOG */}
      <Dialog open={letterDialog} onOpenChange={setLetterDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScrollText size={18} /> Issue Letter — {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Letter Type *</Label>
              <Select value={letterForm.letter_type} onValueChange={v => setLetterForm({ ...letterForm, letter_type: v })}>
                <SelectTrigger className="h-9" data-testid="letter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="experience">Experience Certificate</SelectItem>
                  <SelectItem value="relieving">Relieving Letter</SelectItem>
                  <SelectItem value="appointment">Appointment Letter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{letterForm.letter_type === 'appointment' ? 'Effective Date' : 'End / Relieving Date'}</Label>
                <Input type="date" value={letterForm.relieving_date} onChange={e => setLetterForm({ ...letterForm, relieving_date: e.target.value })} className="h-9" data-testid="letter-date" />
              </div>
              <div>
                <Label className="text-xs">Last Drawn Gross (₹, optional)</Label>
                <Input type="number" min="0" value={letterForm.salary_snapshot} onChange={e => setLetterForm({ ...letterForm, salary_snapshot: e.target.value })} className="h-9 font-mono" data-testid="letter-salary" />
              </div>
            </div>
            {letterForm.letter_type === 'relieving' && (
              <div>
                <Label className="text-xs">Reason (optional)</Label>
                <Input value={letterForm.reason} onChange={e => setLetterForm({ ...letterForm, reason: e.target.value })} placeholder="e.g. Career growth" className="h-9" />
              </div>
            )}
            <div>
              <Label className="text-xs">Remarks (optional)</Label>
              <Textarea value={letterForm.remarks} onChange={e => setLetterForm({ ...letterForm, remarks: e.target.value })} rows={2} placeholder="Any additional notes visible on the letter" data-testid="letter-remarks" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Signatory Name</Label>
                <Input value={letterForm.signatory_name} onChange={e => setLetterForm({ ...letterForm, signatory_name: e.target.value })} placeholder="e.g. Priya Sharma" className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Signatory Designation</Label>
                <Input value={letterForm.signatory_designation} onChange={e => setLetterForm({ ...letterForm, signatory_designation: e.target.value })} className="h-9" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLetterDialog(false)}>Cancel</Button>
            <Button onClick={issueLetter} disabled={issuingLetter} className="bg-brand-primary text-white" data-testid="letter-issue-confirm">
              {issuingLetter ? <Loader2 size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />}
              Issue & Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
